import React, { useEffect, useRef, useState } from "react";
import { Grid } from "components/Grid";
import ActionsBar from "components/SubHeader/ActionsBar";
import SubHeader from "components/SubHeader";
import SubHeaderTitle from "components/SubHeader/SubHeaderTitle";
import { useTranslation } from "react-i18next";
import { netCall } from "utils/netCall";
import { ManagementApiUrl } from "environment/routeSettings";
import { TCity, TJurisdiction, TProvince } from "types/dto";
import { toast } from "react-toastify";
import { AutoComplete } from "components/AutoComplete";
import { useURLTools } from "utils/URLtools";
import SubHeaderFilters from "components/SubHeader/SubHeadeFilters";
import CenterContainer from "components/CenterContainer";
import Spinner from "components/Spinner";
import { AddJurisdictionModal, TAddFormTypes } from "./AddJurisdictionModal";
import { EditJurisdictionModal, TEditFormTypes } from "./EditJurisdictionModal";
import { TResponseCode } from "utils/netCall/types";

const JurisdictionsView = () => {
  const { t } = useTranslation();
  const { queryParams, addUpdateRemoveQueryParams } = useURLTools();
  const [provinces, setProvinces] = useState<TProvince[]>([]);
  const [cities, setCities] = useState<TCity[]>([]);
  const [jurisdictions, setJurisdictions] = useState<TJurisdiction[]>([]);
  const [isProvincesLoading, setIsProvincesLoading] = useState(false);
  const [isCitiesLoading, setIsCitiesLoading] = useState(false);
  const [isJurisdictionsLoading, setIsJurisdictionsLoading] = useState(false);
  const [isSubmitting, setIsSubmiting] = useState(false);
  const [selectedProvince, setSelectedProvince] = useState<TProvince>();
  const [selectedCity, setSelectedCity] = useState<TCity>();
  const [selectedRow, setSelectedRow] = useState<TJurisdiction>();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const GridRef = useRef(null);

  const onAddClick = () => {
    if (selectedProvince && selectedCity) {
      setAddModalOpen(true);
    } else {
      toast.warning(t("selectProvinceAndCity"));
    }
  };
  const onEditClick = (data: any) => {
    if (selectedProvince && selectedCity) {
      setSelectedRow(data.row.data);
      setIsEditModalOpen(true);
    } else {
      toast.warning(t("selectProvinceAndCity"));
    }
  };
  const getProvinceList = async () => {
    setIsProvincesLoading(true);
    const res = await netCall<TProvince[]>({
      method: "POST",
      endPoint: `${ManagementApiUrl()}/api/Province/GetAllProvince`,
    });
    if (res.responseCode === TResponseCode.OK) {
      setProvinces(res.results || []);
      setIsProvincesLoading(false);
    } else {
      setIsProvincesLoading(false);
      toast.warning(t("fetchDataError"));
    }
  };
  const getCitiesList = async (provinceId: string) => {
    setIsCitiesLoading(true);
    const res = await netCall<TProvince>({
      method: "GET",
      endPoint: `${ManagementApiUrl()}/api/Province/GetProvince/${provinceId}`,
    });
    if (res.responseCode === TResponseCode.OK) {
      setCities(res.results.cities || []);
      setIsCitiesLoading(false);
    } else {
      setIsCitiesLoading(false);
      toast.warning(t("fetchDataError"));
    }
  };

  const getJurisdictionsList = async (cityId: string) => {
    setIsJurisdictionsLoading(true);
    const res = await netCall<TCity>({
      method: "GET",
      endPoint: `${ManagementApiUrl()}/api/City/GetCity/${cityId}`,
    });
    if (res.responseCode === TResponseCode.OK) {
      setJurisdictions(res?.results?.jurisdictions || []);
      setIsJurisdictionsLoading(false);
    } else {
      setIsJurisdictionsLoading(false);
      toast.warning(t("fetchDataError"));
    }
  };

  const onSubmitAddJurisdiction = async (values: TAddFormTypes) => {
    if (selectedProvince && selectedCity) {
      console.log(values);
      setIsSubmiting(true);
      const res = await netCall<TProvince>({
        method: "POST",
        endPoint: `${ManagementApiUrl()}/api/Jurisdiction/AddJurisdiction`,
        body: {
          name: values.name,
          provinceId: selectedProvince.id,
          cityId: selectedCity.id,
        },
      });
      if (res.responseCode === TResponseCode.OK) {
        setIsSubmiting(false);
        toast.success(t("operationSuccessful"));
        getJurisdictionsList(selectedCity.id);
        setAddModalOpen(false);
      } else {
        setIsSubmiting(false);
        toast.error(t("operationFailed"));
      }
    }
  };
  const onSubmitEditJurisdiction = async (values: TEditFormTypes) => {
    if (selectedRow && selectedCity && selectedProvince) {
      setIsSubmiting(true);
      const res = await netCall<TCity>({
        method: "POST",
        endPoint: `${ManagementApiUrl()}/api/Jurisdiction/UpdateJurisdiction`,
        body: {
          id: selectedRow.id,
          name: values.name,
          provinceId: selectedProvince.id,
          cityId: selectedCity.id,
        },
      });
      if (res.responseCode === TResponseCode.OK) {
        setIsSubmiting(false);
        toast.success(t("operationSuccessful"));
        getJurisdictionsList(selectedCity.id);
        setIsEditModalOpen(false);
        setSelectedRow(undefined);
      } else {
        setIsSubmiting(false);
        toast.error(t("operationFailed"));
      }
    }
  };
  useEffect(() => {
    const provinceId = queryParams?.provinceId;
    const cityId = queryParams?.cityId;
    if (provinceId && provinces) {
      setSelectedProvince(provinces.find((p) => p.id === provinceId));
    }
    if (cities && cityId) {
      setSelectedCity(cities.find((p) => p.id === cityId));
      getJurisdictionsList(cityId);
    }
  }, [queryParams, provinces, cities]);

  useEffect(() => {
    getProvinceList();
  }, []);

  useEffect(() => {
    if (selectedProvince) {
      getCitiesList(selectedProvince.id);
    }
  }, [selectedProvince]);

  return (
    <>
      <SubHeader>
        <SubHeaderTitle>{t("cities")}</SubHeaderTitle>
        <ActionsBar
          requestTime={0}
          isLoading={false}
          actionButtons={[
            {
              iconName: "MdAdd",
              buttonTooltip: "افزودن",
              action: onAddClick,
            },
          ]}
        />
      </SubHeader>
      <SubHeaderFilters>
        <AutoComplete
          defaultSelected={selectedProvince}
          onSelect={(value: any) => {
            setSelectedProvince(value);
            addUpdateRemoveQueryParams([
              { parameter: "provinceId", value: value?.id },
            ]);
          }}
          optionLabel="name"
          suggestions={provinces}
          placeholder={t("province")}
          hasLabel={false}
          className="w-48"
          onClear={() => {
            setSelectedCity(undefined);
            setSelectedProvince(undefined);
            addUpdateRemoveQueryParams([
              { parameter: "provinceId", value: "" },
              { parameter: "cityId", value: "" },
            ]);
          }}
          loading={isProvincesLoading}
        />
        <AutoComplete
          defaultSelected={selectedCity}
          onSelect={(value: any) => {
            setSelectedCity(value);
            addUpdateRemoveQueryParams([
              { parameter: "cityId", value: value?.id },
            ]);
          }}
          optionLabel="name"
          suggestions={cities}
          placeholder={t("city")}
          hasLabel={false}
          className="w-48"
          onClear={() => {
            setSelectedCity(undefined);
            addUpdateRemoveQueryParams([{ parameter: "cityId", value: "" }]);
          }}
          loading={isCitiesLoading}
          disabled={!selectedProvince}
        />
      </SubHeaderFilters>

      <div className={"w-full flex gap-x h-full"}>
        {isJurisdictionsLoading && (
          <div className="z-[200] absolute bg-mofidGray-40 backdrop-blur-[2px] bg-opacity-30 top-0 left-0 bottom-0 right-0 flex justify-center items-center rounded-md">
            <p>{t("gettingInformation")}</p>
            <Spinner />
          </div>
        )}
        {queryParams?.provinceId && queryParams?.cityId ? (
          <>
            {cities.length === 0 ? (
              <CenterContainer>
                <p className={"text-base"}>{t("noDataToShow")}</p>
              </CenterContainer>
            ) : (
              <Grid
                ref={GridRef}
                gridKey={"id"}
                isLoading={isJurisdictionsLoading}
                dataSource={jurisdictions}
                columns={[
                  {
                    dataField: "id",
                    caption: t("id"),
                  },
                  {
                    dataField: "name",
                    caption: t("name"),
                  },
                  {
                    caption: t("actions"),
                    visible: true,
                    width: 110,
                    buttons: [
                      {
                        icon: "fa-solid fa-pencil",
                        hint: t("edit"),
                        onClick: onEditClick,
                      },
                    ],
                  },
                ]}
                hasSearchPanel={false}
                hasPagination={true}
                hasGroupingFeatures={false}
                hasFilterBuilder={false}
              />
            )}
          </>
        ) : (
          <CenterContainer>
            <p className={"text-base"}>{t("pleaseSelectProvince")}</p>
          </CenterContainer>
        )}
      </div>
      {addModalOpen && (
        <AddJurisdictionModal
          onClose={() => setAddModalOpen(false)}
          isOpen={addModalOpen}
          onSubmit={onSubmitAddJurisdiction}
          province={selectedProvince!}
          city={selectedCity!}
          isLoading={isSubmitting}
        />
      )}
      {isEditModalOpen && (
        <EditJurisdictionModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedRow(undefined);
          }}
          onSubmit={onSubmitEditJurisdiction}
          isLoading={isSubmitting}
          record={selectedRow!}
          province={selectedProvince!}
          city={selectedCity!}
        />
      )}
    </>
  );
};

export default JurisdictionsView;
